import { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Popover from 'react-bootstrap/Popover';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import { BANK_ACCOUNT_STATUS_BADGE } from '../../../../../constants/consumers';
import { Spinner } from '../../../../../components/Spinner';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import classNames from 'classnames';

function ConsumerPopoverBankStatusForm({
  consumerId,
  consumerBankAccountStatus,
  consumerBankAccountStatusMessage,
  refreshConsumer,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [spinner, setSpinner] = useState(false);

  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const handleToggle = () => {
    if (
      consumerBankAccountStatus !== 'NOT_CONNECTED' &&
      consumerBankAccountStatus !== 'NO_TRANSFERS'
    ) {
      setIsOpen(!isOpen);
    }
  };

  const handleBankStatusClick = async bankStatus => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/set_bank_account_status`,
      method: 'POST',
      data: JSON.stringify({ consumerId, status: bankStatus }),
    };

    try {
      await makeRequest(config);
      await refreshConsumer(consumerId);

      toast.success(`Bank Account Status set to ${bankStatus}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
      setIsOpen(false);
    }
  };

  if (!BANK_ACCOUNT_STATUS_BADGE[consumerBankAccountStatus]) {
    return null;
  }

  return (
    <>
      {consumerBankAccountStatusMessage && (
        <OverlayTrigger
          placement="left-start"
          delay={{ show: 200 }}
          popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 4] } }] }}
          overlay={
            <Tooltip className="tooltip-max-width bo-consumer-tooltip-status-message fw-semibold">
              {consumerBankAccountStatusMessage}
            </Tooltip>
          }
        >
          <FontAwesomeIcon
            icon={faCircleInfo}
            color="#f6c244"
            className="me-2"
            id="consumerBankAccountStatusMessage"
          />
        </OverlayTrigger>
      )}
      <OverlayTrigger
        show={isOpen}
        placement="bottom-start"
        popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 6] } }] }}
        overlay={
          <Popover className="custom-popover">
            <Popover.Body className="bo-consumer-popover-form-menu">
              <Spinner visible={spinner} size={36}>
                <div className="d-flex flex-column">
                  {Object.entries(BANK_ACCOUNT_STATUS_BADGE)
                    .filter(([key]) => key !== 'NOT_CONNECTED' && key !== 'NO_TRANSFERS')
                    .map(([key, value]) => (
                      <Button
                        key={key}
                        className="bo-common-base-button mb-2"
                        onClick={() => handleBankStatusClick(key)}
                      >
                        <Badge
                          className={classNames(
                            'bo-inline-badge',
                            'bo-consumer-inline-badge-hover',
                            'shadow-sm',
                            'bo-w-120',
                            value?.color,
                          )}
                        >
                          {value?.text}
                        </Badge>
                      </Button>
                    ))}
                </div>
                <div className="mt-2 d-flex justify-content-center">
                  <Button className="bo-common-button" onClick={handleToggle}>
                    Cancel
                  </Button>
                </div>
              </Spinner>
            </Popover.Body>
          </Popover>
        }
      >
        <Button onClick={handleToggle} className="bo-consumer-popover-form-toggle me-2">
          <Badge
            className={`bo-consumer-inline-badge px-0 bo-w-100
            ${BANK_ACCOUNT_STATUS_BADGE[consumerBankAccountStatus]?.color}`}
          >
            {BANK_ACCOUNT_STATUS_BADGE[consumerBankAccountStatus]?.text}
          </Badge>
        </Button>
      </OverlayTrigger>
    </>
  );
}

export default ConsumerPopoverBankStatusForm;
